import { DateTime } from "luxon";
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { leftPanelActions } from "../../../context/actions/leftPanelActions";
import { modalActions } from "../../../context/actions/modalActions";
import { sidePanelActions } from "../../../context/actions/sidePanelActions";
import useStore from "../../../context/useStore";
import dataService from "../../../helpers/dataService";
import CancelEp from "./CancelEp";
import EPEditDetail from "./EPEditDetail";
import EPModalV2 from "./EpFormV2/EPModalV2";
import { MOIS } from "./calendrierEp";

// ============================================================
// useCalendrierEp — données et actions communes aux deux vues du calendrier
// (classique et compacte). Mêmes appels que l'écran historique :
// POST eps/search { year }, GET sectors, panneau latéral pour une EP V1 et
// pour la création, modale pleine page pour une EP V2, panneau gauche pour
// l'annulation (CancelEp). L'année est partagée : changer de vue la garde.
// ============================================================

export const EP_INITIALE = {
  beginAt: "",
  endedAt: "",
  closedAt: "",
  beginHourAt: "",
  beginMinuteAt: "0",
  sector: "",
  isBackupSession: false,
  jury: [],
  recipients: [],
  address1: "",
  address2: "",
  postalCode: "",
  city: "",
};

export default function useCalendrierEp() {
  const [state, dispatch] = useStore();
  const spActions = sidePanelActions(state, dispatch);
  const lpActions = leftPanelActions(state, dispatch);
  const mActions = modalActions(state, dispatch);

  // « Aujourd'hui » est figé au montage : la page se remonte à chaque visite.
  const maintenant = useMemo(() => DateTime.local(), []);
  const [eps, setEps] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [year, setYear] = useState(maintenant.year);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSavingEp, setIsSavingEp] = useState(false);

  const fetchEps = (annee = year) => {
    setIsLoading(true);
    dataService.post(
      "eps/search",
      { year: annee },
      (datas) => setEps(Array.isArray(datas) ? datas : []),
      setErrors,
      () => setIsLoading(false)
    );
  };

  useEffect(() => {
    dataService.get("sectors", setSectors, setErrors);
  }, []);

  useEffect(() => {
    fetchEps(year);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  const saveEp = (ep) => {
    spActions.updateSidePanel({ isOpen: false, content: null });
    setIsSavingEp(true);
    if (ep.id) {
      dataService.patch(
        `eps/${ep.id}`,
        ep,
        () => {
          toast.success("Modifications enregistrées");
          fetchEps();
        },
        setErrors,
        () => setIsSavingEp(false)
      );
    } else {
      dataService.post(
        "eps",
        ep,
        () => {
          toast.success("EP créée avec succès");
          fetchEps();
        },
        setErrors,
        () => setIsSavingEp(false)
      );
    }
  };

  const removeEp = (ep) => {
    lpActions.updateLeftPanel({
      isOpen: true,
      content: <CancelEp ep={ep} sectors={sectors} onRefreshCalendar={() => fetchEps()} />,
    });
  };

  // indexMois : 0 = janvier. Une EP sans id est une création sur ce mois.
  const openEp = (indexMois, ep) => {
    const monEp = ep.id ? ep : { ...ep, beginAt: DateTime.local(Number(year), indexMois + 1, 1).toISO() };
    if (ep.version === 2) {
      mActions.updateModal({
        isOpen: true,
        noClose: false,
        style: { background: "rgba(0, 0, 0, 0.7)" },
        content: (
          <EPModalV2
            ep={monEp}
            saveEp={saveEp}
            removeEp={removeEp}
            errors={errors}
            setErrors={setErrors}
            sectors={sectors}
            isSavingEp={isSavingEp}
            isRemovingEp={false}
            onRefreshCalendar={() => fetchEps()}
            onClose={() => mActions.updateModal({ isOpen: false, content: null })}
          />
        ),
      });
    } else {
      spActions.updateSidePanel({
        isOpen: true,
        content: (
          <EPEditDetail
            ep={monEp}
            saveEp={saveEp}
            removeEp={removeEp}
            month={MOIS[indexMois]}
            errors={errors}
            setErrors={setErrors}
            sectors={sectors}
            isSavingEp={isSavingEp}
            isRemovingEp={false}
          />
        ),
      });
    }
  };

  const creerEp = (indexMois) => openEp(indexMois, EP_INITIALE);

  return { maintenant, eps, sectors, year, setYear, errors, isLoading, openEp, creerEp };
}
